/* eslint-disable jsx-a11y/alt-text */
import { TxtInput } from "../../../components";
import styled from "@emotion/styled";

import * as React from "react";
import { Project } from "../../../redux/types";
import { useNavigate } from "react-router-dom";
import { Box, Tooltip } from "@mui/material";
import {
  PageContainer,
  ContentWrapper,
  HeaderSection,
  TitleRow,
  PageTitle,
  PageSubtitle,
  SearchInputBox as SearchInput,
  TableWrapper,
  TableRow,
  TableCell,
  PrimaryCell,
  PaginationWrapper,
  PaginationText,
  EmptyState,
  EmptyStateText,
} from "../../../components/table/CommonTable.styled";
import searchIcon from "../../../images/searchIcon.png";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import { useAppSelector } from "../../../redux/hooks";

// Main filters and sub filters mapping (updated)
const MAIN_FILTERS = [
  { label: "All", key: "all" },
  { label: "Project setup", key: "setup" },
  { label: "Project planning", key: "planning" },
  { label: "Ordering", key: "ordering" },
  { label: "Construction", key: "construction" },
  { label: "Completion", key: "completion" },
  { label: "Archive", key: "archive" },
];

const REASONS = [
  "Do not contact",
  "Customer stopped communicating",
  "Project completed",
  "Unable to collect",
  "Cancelled project",
  "Unserviceable Request",
  "Test Project",
  "Other",
];

const SUB_FILTERS_MAP: Record<string, string[]> = {
  all: [],
  setup: ["Filed insurance claim"],
  planning: ["Uploaded Insurance Scope", "Pending Admin Measurement Input"],
  ordering: [
    "Selected materials",
    "Selected Contractors",
    "Signed Estimate",
    "Selected Install Date",
    "Payment Made",
  ],
  construction: [],
  completion: [],
  archive: REASONS,
};

// Styled Components
const SearchIcon = styled.img`
  width: 18px;
  height: 18px;
  margin: 0 12px;
  cursor: pointer;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
`;

const ManageButton = styled(ActionButton)`
  background-color: #4b87ed;
  color: white;

  &:hover {
    background-color: #3a6fd8;
  }

  &:disabled {
    background-color: #e0e0e0;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const OperationButton2 = styled(ActionButton)`
  background-color: #e5e7eb;
  color: #374151;

  &:hover {
    background-color: #d1d5db;
  }
`;

const AddButton = styled(ActionButton)`
  background-color: #4b87ed;
  color: white;

  &:hover {
    background-color: #3a6fd8;
  }
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  border: 1.5px solid ${({ active }) => (active ? '#4b87ed' : '#e5e7eb')};
  background: ${({ active }) => (active ? '#4b87ed' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#374151')};

  &:hover {
    background: ${({ active }) => (active ? '#3a6fd8' : '#f3f4f6')};
    border-color: ${({ active }) => (active ? '#3a6fd8' : '#4b87ed')};
  }
`;

const SubFilterButton = styled.button<{ active?: boolean }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  border: 1.5px ${({ active }) => (active ? 'solid' : 'dashed')} ${({ active }) => (active ? '#4b87ed' : '#d1d5db')};
  background: ${({ active }) => (active ? '#e3edff' : '#fff')};
  color: #4b87ed;

  &:hover {
    background: #f3f4f6;
    border-color: #4b87ed;
  }
`;

// Helper function to get order status for a component type
const getOrderStatus = (orderStatus: any[], componentType: 'MATERIAL' | 'CONTRACTOR') => {
  const componentOrder = orderStatus?.find(order => order.componentType === componentType);
  
  return componentOrder || null;
};

// Helper function to render order status badge
const renderOrderStatusBadge = (orderStatus: any[], componentType: 'MATERIAL' | 'CONTRACTOR') => {
  const status = getOrderStatus(orderStatus, componentType);
  
  if (!status) {
    return (
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
        }}
      >
        <Tooltip title="Order Sent">
          <LocalShippingIcon sx={{ fontSize: 20, color: '#dee2e6' }} />
        </Tooltip>
        <Tooltip title="Order Confirmed">
          <CheckCircleIcon sx={{ fontSize: 20, color: '#dee2e6' }} />
        </Tooltip>
      </div>
    );
  }
  
  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px',
      }}
    >
      {/* Order Sent Icon */}
      <Tooltip title={'Order Sent'}>
        <LocalShippingIcon 
          sx={{ 
            fontSize: 20, 
            color: status.orderSent ? '#28a745' : '#dee2e6',
          }} 
        />
      </Tooltip>
      
      {/* Order Confirmed Icon */}
      <Tooltip title={'Order Confirmed'}>
        <CheckCircleIcon 
          sx={{ 
            fontSize: 20, 
            color: status.orderConfirmed ? '#28a745' : '#dee2e6',
          }} 
        />
      </Tooltip>
    </div>
  );
};

interface UserTableProps {
  projects: Project[];
  mainFilter: string;
  setMainFilter: (key: string) => void;
  subFilter: string | undefined;
  setSubFilter: (key: string | undefined) => void;
  loading?: boolean;
}

export const ProjectTable: React.FC<UserTableProps> = ({
  projects,
  mainFilter,
  setMainFilter,
  subFilter,
  setSubFilter,
  loading,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage] = React.useState(30);
  const [keyword, setKeyword] = React.useState(() => sessionStorage.getItem("projectSearch") || "");
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Sync uncontrolled input DOM value from sessionStorage on mount
  React.useEffect(() => {
    const saved = sessionStorage.getItem("projectSearch");
    if (saved && inputRef.current) {
      inputRef.current.value = saved;
    }
  }, []);

  const handleKeywordChange = (value: string) => {
    setKeyword(value);
    if (value) {
      sessionStorage.setItem("projectSearch", value);
    } else {
      sessionStorage.removeItem("projectSearch");
    }
  };
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: string }>({ key: "customerId", direction: "descending" });

  // Fetch permissions from Redux using useAppSelector
  const permissions = useAppSelector((state) => state.userReducer.permissions);

  const hasWriteAccess = permissions.some(
    (permission) => permission.name === "MANAGE_WORKFLOW" && permission.permission.includes("WRITE")
  );

  const handleSearchIconClick = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleCreateProject = () => {
    navigate("/project-create"); // Navigate to the create project page
  };

  const handleMainFilterChange = (idx: number) => {
    const key = MAIN_FILTERS[idx].key;
    setMainFilter(key);
    setSubFilter(undefined); // Reset sub filter on main change
  };

  const handleSubFilterToggle = (filter: string) => {
    setSubFilter(subFilter === filter ? undefined : filter);
  };

  const sortedProjects = React.useMemo(() => {
    let sortableProjects = Array.isArray(projects) ? [...projects] : [];
    if (sortConfig !== null) {
      sortableProjects.sort((a, b) => {
        let aValue, bValue;
        if (sortConfig.key === "customerName") {
          aValue = `${a.customer?.firstName} ${a.customer?.lastName}`.toLowerCase();
          bValue = `${b.customer?.firstName} ${b.customer?.lastName}`.toLowerCase();
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableProjects;
  }, [projects, sortConfig]);

  const requestSort = (key: string) => {
    let direction = "ascending";
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const filterProjects = () => {
    if (!Array.isArray(sortedProjects)) return [];
    
    const filteredProjects =
      keyword.length > 0
        ? sortedProjects.filter((item) => {
            const fullName =
              `${item.customer?.firstName} ${item.customer?.lastName}`.toLowerCase();
            const projectRefNumber = item.projectRefNumber
              .toString()
              .toLowerCase();
            const customerId = item.customerId?.toString().toLowerCase();
            const createdAt = convertToNormalDate(item.createdAt).toLowerCase();
            return (
              fullName.includes(keyword.toLowerCase()) ||
              projectRefNumber.includes(keyword.toLowerCase()) ||
              customerId.includes(keyword.toLowerCase()) ||
              createdAt.includes(keyword.toLowerCase())
            );
          })
        : sortedProjects;

    return filteredProjects;
  };

  const goToPreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const goToNextPage = () => {
    setPage((prevPage) =>
      Math.min(
        prevPage + 1,
        Math.ceil(filterProjects().length / rowsPerPage) - 1
      )
    );
  };

  function convertToNormalDate(dateString: string) {
    const cleanedDateString = dateString.split(" +")[0];
    const date = new Date(cleanedDateString);

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };
  
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
    return formattedDate.replace(',', ' @');
  }

  const paginatedProjects = filterProjects().slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const totalPages = Math.ceil(filterProjects().length / rowsPerPage);

  const mainIdx = MAIN_FILTERS.findIndex((f) => f.key === mainFilter);
  const subFilters = SUB_FILTERS_MAP[mainFilter] || [];

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Header */}
        <HeaderSection>
          <TitleRow>
            <PageTitle>Projects</PageTitle>
          </TitleRow>
          <PageSubtitle>{filterProjects().length} projects</PageSubtitle>
        </HeaderSection>

        {/* Main Filters */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          {MAIN_FILTERS.map((filter, idx) => (
            <FilterButton
              key={filter.key}
              active={mainIdx === idx}
              onClick={() => handleMainFilterChange(idx)}
            >
              {filter.label}
            </FilterButton>
          ))}
        </Box>

        {/* Sub Filters */}
        {subFilters.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {subFilters.map((filter) => (
              <SubFilterButton
                key={filter}
                active={subFilter === filter}
                onClick={() => handleSubFilterToggle(filter)}
              >
                {filter}
              </SubFilterButton>
            ))}
          </Box>
        )}

        {/* Search and Add Button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mb: 3,
          }}
        >
          <SearchInput>
            <SearchIcon
              src={searchIcon}
              onClick={() => inputRef.current?.focus()}
            />
            <TxtInput
              placeholder="Search customer name"
              onChange={handleKeywordChange}
              inputRef={inputRef}
              background="white"
              width="100%"
            />
          </SearchInput>
          <AddButton onClick={handleCreateProject}>+ Create Project</AddButton>
        </Box>

        {/* Table */}
        <TableWrapper style={{ position: "relative" }}>
          {/* Desktop Table Header - Hidden on mobile */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              padding: "14px 20px",
              backgroundColor: "#f3f4f6",
              borderBottom: "1px solid #e5e7eb",
              fontWeight: 600,
              fontSize: 12,
              color: "#6b7280",
              textTransform: "uppercase",
            }}
          >
            <span 
              style={{ width: "10%", cursor: "pointer" }} 
              onClick={() => requestSort("projectRefNumber")}
            >
              Ref # {sortConfig.key === "projectRefNumber" && (sortConfig.direction === "ascending" ? "↑" : "↓")}
            </span>
            <span 
              style={{ width: "12%", cursor: "pointer" }} 
              onClick={() => requestSort("customerId")}
            >
              Customer ID {sortConfig.key === "customerId" && (sortConfig.direction === "ascending" ? "↑" : "↓")}
            </span>
            <span 
              style={{ width: "18%", cursor: "pointer" }} 
              onClick={() => requestSort("customerName")}
            >
              Customer Name {sortConfig.key === "customerName" && (sortConfig.direction === "ascending" ? "↑" : "↓")}
            </span>
            <span 
              style={{ width: "20%", cursor: "pointer" }} 
              onClick={() => requestSort("createdAt")}
            >
              Created At {sortConfig.key === "createdAt" && (sortConfig.direction === "ascending" ? "↑" : "↓")}
            </span>
            <span style={{ width: "10%", textAlign: "center" }}>Material</span>
            <span style={{ width: "10%", textAlign: "center" }}>Contractor</span>
            <span style={{ width: "20%" }}>Actions</span>
          </Box>

          {/* Table Body */}
          {paginatedProjects.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No projects found</EmptyStateText>
            </EmptyState>
          ) : (
            paginatedProjects.map((project) => (
              <TableRow key={project.id}>
                <PrimaryCell width="10%" mobileLabel="Ref #">
                  {project.projectRefNumber}
                </PrimaryCell>
                <TableCell width="12%" mobileLabel="Customer ID">
                  {project.customerId}
                </TableCell>
                <TableCell width="18%" mobileLabel="Customer Name">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {project.customer?.firstName} {project.customer?.lastName}
                    {project.MeasurementSupportRequested && (
                      <Tooltip title="Measurement Support Requested">
                        <NotificationImportantIcon 
                          sx={{ 
                            fontSize: 22, 
                            color: '#ff9800',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }} 
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell width="20%" mobileLabel="Created At">
                  {convertToNormalDate(project.createdAt)}
                </TableCell>
                <TableCell width="10%" mobileLabel="Material Order">
                  <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "center" } }}>
                    {renderOrderStatusBadge(project.orderStatus || [], "MATERIAL")}
                  </Box>
                </TableCell>
                <TableCell width="10%" mobileLabel="Contractor Order">
                  <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "center" } }}>
                    {renderOrderStatusBadge(project.orderStatus || [], "CONTRACTOR")}
                  </Box>
                </TableCell>
                <TableCell width="20%" mobileLabel="Actions">
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <ManageButton
                      disabled={!hasWriteAccess}
                      onClick={() => navigate(`/manage-project/${project.id}`)}
                    >
                      Manage
                    </ManageButton>
                    <OperationButton2 onClick={() => navigate(`/projects/${project.id}`)}>
                      Operation
                    </OperationButton2>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}

          {/* Pagination */}
          <PaginationWrapper>
            <Box
              component="button"
              onClick={goToPreviousPage}
              disabled={page === 0}
              sx={{
                px: 2,
                py: 1,
                border: "none",
                borderRadius: "6px",
                backgroundColor: page === 0 ? "#f3f4f6" : "#e5e7eb",
                color: page === 0 ? "#9ca3af" : "#374151",
                fontSize: 13,
                fontWeight: 500,
                cursor: page === 0 ? "not-allowed" : "pointer",
                "&:hover": page === 0 ? {} : { backgroundColor: "#d1d5db" },
              }}
            >
              ← Prev
            </Box>
            <PaginationText>
              Page {page + 1} of {totalPages || 1}
            </PaginationText>
            <Box
              component="button"
              onClick={goToNextPage}
              disabled={page >= totalPages - 1}
              sx={{
                px: 2,
                py: 1,
                border: "none",
                borderRadius: "6px",
                backgroundColor: page >= totalPages - 1 ? "#f3f4f6" : "#e5e7eb",
                color: page >= totalPages - 1 ? "#9ca3af" : "#374151",
                fontSize: 13,
                fontWeight: 500,
                cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                "&:hover": page >= totalPages - 1 ? {} : { backgroundColor: "#d1d5db" },
              }}
            >
              Next →
            </Box>
          </PaginationWrapper>

          {/* Loading Overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.8)",
                zIndex: 10,
              }}
            >
              <Box
                sx={{
                  padding: "12px 24px",
                  borderRadius: "8px",
                  background: "#fff",
                  color: "#4b87ed",
                  fontWeight: 600,
                  fontSize: 16,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                Loading...
              </Box>
            </Box>
          )}
        </TableWrapper>
      </ContentWrapper>
    </PageContainer>
  );
};

